import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CustomContainer from '../../components/CustomContainer';
import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import EmptyState from '../../components/EmptyState';
import AppCard from '../../components/ui/AppCard';
import Eyebrow from '../../components/ui/Eyebrow';
import { SpecItem, SpecList } from '../../components/ui/SpecList';
import { FaImage, FaPaperPlane, FaTrash, FaUser } from 'react-icons/fa6';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getQuoteDetails,
    approveQuote,
    getQuoteMessages,
    postQuoteMessage,
} from '../../helpers/httpRequests/quotes';
import { Link, useParams, useNavigate } from 'react-router-dom';
import LoadingComponent from '../../components/LoadingComponent';
import { getCategories } from '../../helpers/httpRequests/product_props';
import { useModal } from '../../contexts/ModalProvider';
import { metalList, resolveQuoteStatus } from '../../utils/variables';
import { formatOptions } from '../../utils/functions';
import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import appToast from '../../components/appToast';
import ConfirmDialog from '../../components/ConfirmDialog';

const MAX_IMAGES = 3;

function isEmptyValue(val) {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string' && val.trim() === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    return false;
}

function optionLabel(options, value) {
    if (isEmptyValue(value)) return null;
    const found = options?.find((opt) => String(opt.value) === String(value));
    return found?.label || String(value);
}

function formatDate(value) {
    if (isEmptyValue(value)) return null;
    const parsed = dayjs(value);
    if (parsed.isValid() && /\d{4}/.test(String(value))) {
        return parsed.format('DD/MM/YYYY');
    }
    return String(value);
}

function formatMessageTime(value) {
    if (isEmptyValue(value)) return '';
    const raw = String(value);
    // API devolve "DD/MM/YYYY HH:MM". Mantém como está.
    if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(raw)) {
        return raw;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : raw;
}

function fileToSquareJpeg(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
        reader.onload = (event) => {
            const img = new Image();
            img.onerror = () => reject(new Error('Arquivo de imagem inválido.'));
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const size = 500;
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                const aspectRatio = img.width / img.height;
                let drawWidth;
                let drawHeight;
                let offsetX;
                let offsetY;

                if (aspectRatio > 1) {
                    drawWidth = size * aspectRatio;
                    drawHeight = size;
                    offsetX = -(drawWidth - size) / 2;
                    offsetY = 0;
                } else {
                    drawWidth = size;
                    drawHeight = size / aspectRatio;
                    offsetX = 0;
                    offsetY = -(drawHeight - size) / 2;
                }

                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                resolve(canvas.toDataURL('image/jpeg'));
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
}

function MessageBubble({ message, isMine, onOpenImage, onApprove, approvingId, completed }) {
    const isProposal = message.message_type === 'proposal';

    if (isProposal) {
        return (
            <Box sx={{ ...styles.row, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                {!isMine && (
                    <Avatar src={message.author_image || undefined} sx={styles.msgAvatar}>
                        <FaUser size={12} />
                    </Avatar>
                )}
                <Box sx={styles.proposalCard}>
                    <Eyebrow>Proposta</Eyebrow>
                    <Box sx={styles.proposalBody}>
                        {message.product_image && (
                            <Box
                                component="button"
                                type="button"
                                onClick={() => onOpenImage(message.product_image)}
                                sx={styles.proposalThumbBtn}
                            >
                                <Box
                                    component="img"
                                    src={message.product_image}
                                    alt={message.product_name || 'Produto'}
                                    sx={styles.proposalThumb}
                                />
                            </Box>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={styles.proposalTitle}>
                                {message.product_name || `Produto #${message.product_id}`}
                            </Typography>
                            {message.author_name && (
                                <Typography sx={styles.proposalMeta}>
                                    {message.author_name}
                                    {message.created_at ? `, ${formatMessageTime(message.created_at)}` : ''}
                                </Typography>
                            )}
                            {!isEmptyValue(message.body) && (
                                <Typography sx={styles.proposalObs}>{message.body}</Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={styles.proposalActions}>
                        {!completed && (
                            <Button
                                onClick={() => onApprove(message.product_id)}
                                variant="contained"
                                color="secondary"
                                size="small"
                                disabled={approvingId === message.product_id}
                            >
                                {approvingId === message.product_id ? 'Abrindo…' : 'Aprovar e encomendar'}
                            </Button>
                        )}
                        <Button
                            component={Link}
                            to={`/produto-detalhes/${message.product_id}`}
                            variant="outlined"
                            color="primary"
                            size="small"
                        >
                            Ver detalhes
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ ...styles.row, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
            {!isMine && (
                <Avatar src={message.author_image || undefined} sx={styles.msgAvatar}>
                    <FaUser size={12} />
                </Avatar>
            )}
            <Box sx={{ ...styles.bubble, ...(isMine ? styles.bubbleMine : styles.bubbleTheirs) }}>
                {!isMine && message.author_name && (
                    <Typography sx={styles.bubbleAuthor}>{message.author_name}</Typography>
                )}
                {!isEmptyValue(message.body) && (
                    <Typography sx={styles.bubbleText}>{message.body}</Typography>
                )}
                {Array.isArray(message.images) && message.images.length > 0 && (
                    <Box sx={styles.msgGallery}>
                        {message.images.map((img, idx) => (
                            <Box
                                key={`${message.id}-img-${idx}`}
                                component="button"
                                type="button"
                                onClick={() => onOpenImage(img)}
                                sx={styles.msgThumbBtn}
                            >
                                <Box component="img" src={img} alt="" sx={styles.msgThumb} />
                            </Box>
                        ))}
                    </Box>
                )}
                <Typography sx={styles.bubbleTime}>{formatMessageTime(message.created_at)}</Typography>
            </Box>
        </Box>
    );
}

export default function QuoteDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setImage, handleOpen } = useModal();
    const [approvingId, setApprovingId] = useState(null);
    const [pendingProductId, setPendingProductId] = useState(null);
    const [draft, setDraft] = useState('');
    const [draftImages, setDraftImages] = useState([]);
    const fileInputRef = useRef(null);
    const threadEndRef = useRef(null);

    const categoriesQuery = useQuery({
        queryKey: ['getCategories'],
        queryFn: getCategories,
        staleTime: 1000 * 60,
    });
    const quoteQuery = useQuery({
        queryKey: ['getQuoteDetails', id],
        queryFn: () => getQuoteDetails(id),
        staleTime: 1000 * 60,
    });

    const quote = quoteQuery.data?.data?.data;
    const completed = Boolean(quote?.completed_at);

    const messagesQuery = useQuery({
        queryKey: ['getQuoteMessages', id],
        queryFn: () => getQuoteMessages(id),
        enabled: Boolean(id),
        refetchInterval: completed ? false : 15000,
        staleTime: 5000,
    });

    const categories = formatOptions(categoriesQuery.data);
    const images = useMemo(
        () => (Array.isArray(quote?.images) ? quote.images.filter((image) => !isEmptyValue(image)) : []),
        [quote],
    );
    const status = resolveQuoteStatus(quote);
    const messages = useMemo(
        () => messagesQuery.data?.data?.data?.messages || [],
        [messagesQuery.data],
    );

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    const openImage = (image) => {
        setImage(image);
        handleOpen();
    };

    const sendMutation = useMutation({
        mutationFn: postQuoteMessage,
        onSuccess: () => {
            setDraft('');
            setDraftImages([]);
            queryClient.invalidateQueries({ queryKey: ['getQuoteMessages', id] });
            queryClient.invalidateQueries({ queryKey: ['getQuoteDetails', id] });
        },
        onError: (err) => {
            const msg = err?.response?.data?.message || 'Não foi possível enviar a mensagem.';
            appToast.error(msg);
        },
    });

    const requestApprove = (productId) => {
        if (!productId) return;
        setPendingProductId(productId);
    };

    const handleApprove = async () => {
        const productId = pendingProductId;
        if (!productId) return;
        setApprovingId(productId);
        try {
            await approveQuote(quote.id, productId);
            queryClient.invalidateQueries({ queryKey: ['getQuoteDetails', id] });
            queryClient.invalidateQueries({ queryKey: ['getQuoteMessages', id] });
            queryClient.invalidateQueries({ queryKey: ['getQuotes'] });
        } catch (e) {
            // Aprovação é carimbo; segue para especificação mesmo se falhar.
        } finally {
            setPendingProductId(null);
            navigate('/pedido-especificacoes/' + productId);
        }
    };

    const handlePickImages = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;
        const remaining = MAX_IMAGES - draftImages.length;
        if (remaining <= 0) {
            appToast.error(`Máximo de ${MAX_IMAGES} imagens por mensagem.`);
            return;
        }
        try {
            const sliced = files.slice(0, remaining);
            const converted = await Promise.all(sliced.map(fileToSquareJpeg));
            setDraftImages((prev) => [...prev, ...converted]);
        } catch (e) {
            appToast.error('Não foi possível processar a imagem.');
        }
    };

    const handleSend = () => {
        const body = draft.trim();
        if (!body && draftImages.length === 0) return;
        sendMutation.mutate({
            quote_id: Number(id),
            body,
            images: draftImages,
        });
    };

    if (categoriesQuery.isFetching || quoteQuery.isFetching) {
        return <LoadingComponent />;
    }

    if (!quote?.id) {
        return (
            <CustomContainer>
                <Box sx={{ my: 4 }}>
                    <PageHeader
                        title="Orçamento"
                        crumbs={[{ label: 'Meus orçamentos', to: '/orcamentos' }, 'Detalhes']}
                    />
                    <AppCard>
                        <EmptyState
                            title="Orçamento não encontrado"
                            description="Esse orçamento não está disponível ou o link está incompleto."
                            actionLabel="Ver meus orçamentos"
                            actionTo="/orcamentos"
                        />
                    </AppCard>
                </Box>
            </CustomContainer>
        );
    }

    return (
        <CustomContainer>
            <Box sx={{ my: 4 }}>
                <PageHeader
                    title={quote.name || `Orçamento #${quote.id}`}
                    crumbs={[{ label: 'Meus orçamentos', to: '/orcamentos' }, `Orçamento #${quote.id}`]}
                    action={
                        <StatusChip
                            color={status?.color}
                            bg={status?.bg}
                            label={status?.label}
                        />
                    }
                />

                <AppCard sx={{ mb: 4 }}>
                    {images.length > 0 && (
                        <Box sx={styles.gallery}>
                            {images.map((image, index) => (
                                <Box
                                    key={`${image}-${index}`}
                                    component="button"
                                    type="button"
                                    onClick={() => openImage(image)}
                                    sx={styles.thumbBtn}
                                >
                                    <Box
                                        component="img"
                                        src={image}
                                        alt={`${quote.name || 'Joia'}, foto ${index + 1}`}
                                        sx={styles.thumb}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                    <SpecList columns={3}>
                        <SpecItem label="Orçamento" value={`#${quote.id}`} />
                        <SpecItem label="Categoria" value={optionLabel(categories, quote.category_id)} />
                        <SpecItem label="Metal" value={optionLabel(metalList, quote.selected_metal)} />
                        <SpecItem label="Solicitado em" value={formatDate(quote.created_at)} />
                    </SpecList>
                    {!isEmptyValue(quote.description) && (
                        <Box sx={{ mt: 3 }}>
                            <SpecList columns={1}>
                                <SpecItem label="Descrição" value={quote.description} wide />
                            </SpecList>
                        </Box>
                    )}
                </AppCard>

                <AppCard sx={{ mb: 2 }}>
                    <Eyebrow>Conversa</Eyebrow>
                    <Typography variant="h4" sx={{ mt: 1, mb: 2, textAlign: 'left' }}>
                        Mensagens
                    </Typography>

                    <Box sx={styles.thread}>
                        {messagesQuery.isFetching && messages.length === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        )}
                        {!messagesQuery.isFetching && messages.length === 0 && (
                            <Typography sx={styles.emptyThread}>
                                Ainda sem mensagens. Envie uma dúvida ou aguarde a equipe.
                            </Typography>
                        )}
                        {messages.map((msg) => (
                            <MessageBubble
                                key={msg.id}
                                message={msg}
                                isMine={msg.author_type === 'customer'}
                                onOpenImage={openImage}
                                onApprove={requestApprove}
                                approvingId={approvingId}
                                completed={completed}
                            />
                        ))}
                        <div ref={threadEndRef} />
                    </Box>

                    {completed ? (
                        <Typography sx={styles.completedNote}>
                            Este orçamento foi concluído. A conversa está somente para leitura.
                        </Typography>
                    ) : (
                        <Box sx={styles.composer}>
                            {draftImages.length > 0 && (
                                <Box sx={styles.composerPreviews}>
                                    {draftImages.map((img, idx) => (
                                        <Box key={`draft-${idx}`} sx={styles.composerPreviewWrap}>
                                            <Box component="img" src={img} alt="" sx={styles.composerPreview} />
                                            <IconButton
                                                size="small"
                                                aria-label="Remover imagem"
                                                onClick={() =>
                                                    setDraftImages((prev) => prev.filter((_, i) => i !== idx))
                                                }
                                                sx={styles.composerRemove}
                                            >
                                                <FaTrash size={12} />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Box sx={styles.composerRow}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={handlePickImages}
                                />
                                <IconButton
                                    color="primary"
                                    aria-label="Anexar imagem"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={sendMutation.isPending || draftImages.length >= MAX_IMAGES}
                                >
                                    <FaImage />
                                </IconButton>
                                <TextField
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    placeholder="Escreva uma mensagem…"
                                    fullWidth
                                    multiline
                                    maxRows={4}
                                    size="small"
                                    disabled={sendMutation.isPending}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                />
                                <IconButton
                                    color="secondary"
                                    aria-label="Enviar"
                                    onClick={handleSend}
                                    disabled={
                                        sendMutation.isPending ||
                                        (!draft.trim() && draftImages.length === 0)
                                    }
                                >
                                    {sendMutation.isPending ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <FaPaperPlane />
                                    )}
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </AppCard>
            </Box>
            <ConfirmDialog
                open={Boolean(pendingProductId)}
                title="Aprovar esta proposta?"
                description="O orçamento é marcado como aprovado e você segue para especificar a encomenda desta peça."
                confirmLabel="Aprovar e encomendar"
                cancelLabel="Voltar"
                loading={Boolean(approvingId)}
                onCancel={() => {
                    if (!approvingId) setPendingProductId(null);
                }}
                onConfirm={handleApprove}
            />
        </CustomContainer>
    );
}

const styles = {
    gallery: {
        display: 'flex',
        gap: 1.5,
        overflowX: 'auto',
        pb: 0.5,
        mb: 3,
    },
    thumbBtn: {
        flex: '0 0 auto',
        p: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        cursor: 'pointer',
        overflow: 'hidden',
        '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'secondary.main',
            outlineOffset: 2,
        },
    },
    thumb: {
        display: 'block',
        width: 120,
        height: 120,
        objectFit: 'cover',
        aspectRatio: '1 / 1',
    },
    thread: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        maxHeight: 420,
        overflowY: 'auto',
        py: 1,
        mb: 2,
    },
    emptyThread: {
        color: 'text.secondary',
        textAlign: 'left',
        py: 2,
    },
    row: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        width: '100%',
    },
    msgAvatar: {
        width: 32,
        height: 32,
        bgcolor: 'background.default',
        color: 'text.secondary',
        flexShrink: 0,
    },
    bubble: {
        maxWidth: 'min(100%, 28rem)',
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        textAlign: 'left',
    },
    bubbleMine: {
        bgcolor: 'secondary.main',
        color: 'secondary.contrastText',
        borderBottomRightRadius: 4,
    },
    bubbleTheirs: {
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderBottomLeftRadius: 4,
    },
    bubbleAuthor: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mb: 0.5,
    },
    bubbleText: {
        whiteSpace: 'pre-wrap',
        fontSize: '0.9375rem',
        lineHeight: 1.45,
    },
    bubbleTime: {
        mt: 0.75,
        fontSize: '0.6875rem',
        opacity: 0.75,
        fontVariantNumeric: 'tabular-nums',
    },
    msgGallery: {
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        mt: 1,
    },
    msgThumbBtn: {
        p: 0,
        border: 0,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: 'transparent',
    },
    msgThumb: {
        display: 'block',
        width: 72,
        height: 72,
        objectFit: 'cover',
    },
    proposalCard: {
        maxWidth: 'min(100%, 32rem)',
        width: '100%',
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        textAlign: 'left',
    },
    proposalBody: {
        display: 'flex',
        gap: 1.5,
        mt: 1,
        mb: 2,
    },
    proposalThumbBtn: {
        p: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: 'transparent',
        flexShrink: 0,
    },
    proposalThumb: {
        display: 'block',
        width: 72,
        height: 72,
        objectFit: 'cover',
    },
    proposalTitle: {
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    proposalMeta: {
        fontSize: '0.75rem',
        color: 'text.secondary',
        mt: 0.25,
    },
    proposalObs: {
        mt: 1,
        whiteSpace: 'pre-wrap',
        fontSize: '0.875rem',
        color: 'text.primary',
    },
    proposalActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
    },
    composer: {
        borderTop: '1px solid',
        borderColor: 'divider',
        pt: 2,
    },
    composerPreviews: {
        display: 'flex',
        gap: 1,
        mb: 1.5,
        flexWrap: 'wrap',
    },
    composerPreviewWrap: {
        position: 'relative',
    },
    composerPreview: {
        width: 56,
        height: 56,
        objectFit: 'cover',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
    },
    composerRemove: {
        position: 'absolute',
        top: -8,
        right: -8,
        bgcolor: 'background.paper',
        boxShadow: 1,
        '&:hover': { bgcolor: 'background.paper' },
    },
    composerRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
    },
    completedNote: {
        borderTop: '1px solid',
        borderColor: 'divider',
        pt: 2,
        color: 'text.secondary',
        textAlign: 'left',
        fontSize: '0.875rem',
    },
};
